/**
 * usePageLayout(slug)
 *
 * Fetches GET /api/pages/:slug and returns the layout array plus the
 * optional featuredLibraries map (keyed by section id).
 *
 * Falls back to DEFAULT_LAYOUTS[slug] on any error, empty response,
 * or non-array result — so the caller always receives a usable array.
 *
 * Returns { layout: SectionObject[], featuredLibraries: Object, loading: boolean }
 *
 * featuredLibraries: { [sectionId]: LibraryCardObject[] }
 * When present for a given section id, the renderer should prefer these
 * server-resolved libraries over the dynamic top-libraries list.
 */

import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { DEFAULT_LAYOUTS } from '../editable/defaultLayout';

function usePageLayout(slug) {
  const defaultLayout = DEFAULT_LAYOUTS[slug] || [];
  const [layout, setLayout] = useState(defaultLayout);
  const [featuredLibraries, setFeaturedLibraries] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setLayout(defaultLayout);
      setFeaturedLibraries({});
      setLoading(false);
      return;
    }

    let cancelled = false;

    api
      .get(`/pages/${slug}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        const remoteLayout = data?.layout;
        if (Array.isArray(remoteLayout) && remoteLayout.length > 0) {
          setLayout(remoteLayout);
        } else {
          setLayout(defaultLayout);
        }
        // Capture the server-resolved featured libraries map (may be absent)
        setFeaturedLibraries(
          data?.featuredLibraries && typeof data.featuredLibraries === 'object'
            ? data.featuredLibraries
            : {}
        );
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLayout(defaultLayout);
        setFeaturedLibraries({});
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // defaultLayout is a stable reference from module scope — not a dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return { layout, featuredLibraries, loading };
}

export default usePageLayout;
